import { Component, OnInit, signal, inject, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { SwUpdate } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { LanguageService } from './services/language.service';
import { environment } from '../environments/environment';

const APP_VERSION_STORAGE_KEY = 'appVersion';

@Component({
  selector: 'app-root',
  imports: [IonApp, IonRouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  protected readonly title = signal('easywy-app');
  private readonly location = inject(Location);
  private readonly router = inject(Router);

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private languageService: LanguageService,
    private updates: SwUpdate
  ) {
    // On native mobile: clear cache when app is updated (new version installed) so old cached data is removed
    if (isPlatformBrowser(this.platformId) && Capacitor.isNativePlatform() && typeof localStorage !== 'undefined') {
      const storedVersion = localStorage.getItem(APP_VERSION_STORAGE_KEY);
      if (storedVersion !== environment.appVersion) {
        localStorage.clear();
        sessionStorage.clear();
        localStorage.setItem(APP_VERSION_STORAGE_KEY, environment.appVersion);
      }
    }

    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        this.languageService.initFromUser(user);
      } catch {
        this.languageService.initFromStorage();
      }
    } else {
      this.languageService.initFromStorage();
    }
  }

  ngOnInit(): void {
    // Only show "New version available" in browser; on mobile, updates come from the app store
    if (
      this.updates.isEnabled &&
      isPlatformBrowser(this.platformId) &&
      !Capacitor.isNativePlatform()
    ) {
      this.updates.versionUpdates.subscribe((event) => {
        if (event.type === 'VERSION_READY') {
          const confirmRefresh = confirm('New version available. Load new version?');
          if (confirmRefresh) {
            window.location.reload();
          }
        }
      });
    }

    // Android: hardware back — use Capacitor's canGoBack (history.length is unreliable in WebView).
    // On dashboard and login (/home), send app to background (root screens; avoid leaving auth via history).
    if (isPlatformBrowser(this.platformId) && Capacitor.getPlatform() === 'android') {
      CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        const path = this.router.url.split('?')[0].split('#')[0];
        const normalizedPath = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
        if (
          normalizedPath === '/dashboard' ||
          normalizedPath === '/main/dashboard' ||
          normalizedPath === '/home'
        ) {
          void CapacitorApp.minimizeApp();
          return;
        }
        if (canGoBack) {
          this.location.back();
        } else {
          void CapacitorApp.minimizeApp();
        }
      });
    }
  }
}
